// 修改 ResponsiveModal.tsx - 修復網頁模式下高度問題
import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface ResponsiveModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: ReactNode;
    showHeader?: boolean;
    rightAction?: ReactNode;
    leftAction?: ReactNode;
    headerContent?: ReactNode;

    // 新增統一的 Save 按鈕支持
    saveButton?: {
        onSave: () => void;
        disabled?: boolean;
        isSubmitting?: boolean;
        text?: string;
        submittingText?: string;
    };
}

const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    showHeader = true,
    rightAction,
    leftAction,
    headerContent,
    saveButton
}) => {
    // 用於控制模態框的動畫和可見性
    const [isVisible, setIsVisible] = useState(false);

    // 檢測是否為 PWA 模式
    const [isPWA, setIsPWA] = useState(false);

    useEffect(() => {
        // 檢測 PWA 模式的多種方法
        const checkPWAMode = () => {
            // 方法 1: 檢查 display-mode
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

            // 方法 2: 檢查 navigator.standalone (iOS Safari)
            const isIOSStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

            // 方法 3: 檢查是否在 WebView 中 (Android)
            const isAndroidWebView = window.matchMedia('(display-mode: fullscreen)').matches;

            return isStandalone || isIOSStandalone || isAndroidWebView;
        };

        setIsPWA(checkPWAMode());

        // 監聽 display-mode 變化
        const mediaQuery = window.matchMedia('(display-mode: standalone)');
        const handleDisplayModeChange = (e: MediaQueryListEvent) => {
            setIsPWA(e.matches);
        };

        mediaQuery.addEventListener('change', handleDisplayModeChange);

        return () => {
            mediaQuery.removeEventListener('change', handleDisplayModeChange);
        };
    }, []);

    // 處理滾動鎖定和動畫
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setIsVisible(true);
        } else {
            // 當關閉時，先保持可見以執行關閉動畫
            timeoutId = setTimeout(() => {
                setIsVisible(false);
            }, 300); // 與 CSS 過渡時間匹配
            document.body.style.overflow = '';
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // 點擊背景時關閉模態框
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen && !isVisible) return null;

    // 根據 PWA 模式動態計算高度類別
    const getHeightClasses = () => {
        if (isPWA) {
            // PWA 模式：使用原來的高度
            return 'h-[95vh] md:h-[90vh]';
        } else {
            // 網頁模式：使用 min-h + max-h 組合
            // - h-auto: 讓容器根據內容自動調整
            // - min-h-[400px]: 避免內容太少時 Modal 過小
            // - max-h-[85vh]: 限制最大高度，內容多時啟用滾動
            return 'h-[90vh] md:min-h-[400px] md:max-h-[85vh]';
        }
    };

    // 根據 PWA 模式動態計算 backdrop 的 padding
    const getBackdropClasses = () => {
        if (isPWA) {
            // PWA 模式：無需額外間距
            return 'fixed inset-0 bg-black bg-opacity-40 z-modal flex items-end md:items-center justify-center overflow-y-auto';
        } else {
            // 網頁模式：添加頂部間距避免被瀏覽器 UI 遮擋
            return 'fixed inset-0 bg-black bg-opacity-40 z-modal flex items-end md:items-center justify-center overflow-y-auto pt-2 md:pt-4';
        }
    };

    // 使用 Portal 將 Modal 渲染到 document.body
    return createPortal(
        <div
            className={`${getBackdropClasses()} transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
            onClick={handleBackdropClick}
        >
            <div
                className={`
                    bg-white w-full md:w-[680px] md:max-w-4xl
                    ${getHeightClasses()}
                    rounded-t-xl md:rounded-lg
                    overflow-hidden flex flex-col shadow-xl
                    transform transition-transform duration-300 ease-out
                    ${isOpen ? 'translate-y-0 md:translate-y-0 md:opacity-100' : 'translate-y-full md:translate-y-8 md:opacity-0'}
                    relative md:relative
                `}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 標題列 */}
                {showHeader && (
                    <div className="px-4 min-h-14 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-context-menu">
                        {/* 左側按鈕 */}
                        <div className="w-12 md:w-16 flex items-center justify-start flex-shrink-0">
                            {leftAction}
                        </div>

                        {/* 中間內容區域 - 如果有 headerContent 則顯示它，否則顯示標題 */}
                        <div className="flex-1 flex items-center justify-center min-w-0 px-2">
                            {headerContent ? headerContent : (
                                <h2 className="text-lg md:text-xl font-medium text-gray-800 truncate">
                                    {title}
                                </h2>
                            )}
                        </div>

                        {/* 右側按鈕 */}
                        <div className="w-12 md:w-16 flex items-center justify-end flex-shrink-0">
                            {rightAction || (saveButton && (
                                <button
                                    onClick={saveButton.onSave}
                                    disabled={saveButton.disabled || saveButton.isSubmitting}
                                    className={`text-sky-500 font-medium ${saveButton.disabled || saveButton.isSubmitting
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:text-sky-600'
                                        }`}
                                    type="button"
                                >
                                    {saveButton.isSubmitting
                                        ? (saveButton.submittingText || 'Saving...')
                                        : (saveButton.text || 'Save')
                                    }
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* 內容區域 - 可滾動 */}
                <div className="flex-1 overflow-y-auto bg-white hide-scrollbar">
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ResponsiveModal;